import React, { forwardRef, InputHTMLAttributes, ReactNode, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

/**
 * Input variants using class-variance-authority for type-safe styling
 */
const inputVariants = cva(
  // Base styles
  `
    w-full
    bg-white dark:bg-neutral-900
    border border-neutral-300 dark:border-neutral-700
    text-neutral-900 dark:text-neutral-100
    placeholder:text-neutral-400 dark:placeholder:text-neutral-500
    transition-all duration-200 ease-smooth
    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
    dark:focus:ring-primary-400 dark:focus:border-primary-400
    disabled:opacity-50 disabled:cursor-not-allowed
    disabled:bg-neutral-50 dark:disabled:bg-neutral-800
  `,
  {
    variants: {
      variant: {
        // Default - Standard input appearance
        default: `
          hover:border-neutral-400 dark:hover:border-neutral-600
        `,
        
        // Filled - More prominent background
        filled: `
          bg-neutral-50 dark:bg-neutral-800
          border-transparent
          hover:bg-neutral-100 dark:hover:bg-neutral-700
          focus:bg-white dark:focus:bg-neutral-900
          focus:border-primary-500 dark:focus:border-primary-400
        `,
        
        // Outline - Prominent border
        outlined: `
          border-2
          hover:border-primary-300 dark:hover:border-primary-700
        `,
        
        // Ghost - Minimal styling
        ghost: `
          border-transparent
          hover:border-neutral-300 dark:hover:border-neutral-700
          focus:border-primary-500 dark:focus:border-primary-400
        `,
      },
      
      size: {
        sm: 'h-8 px-3 text-sm rounded-md',
        md: 'h-10 px-4 text-sm rounded-lg',
        lg: 'h-12 px-4 text-base rounded-lg',
        xl: 'h-14 px-6 text-lg rounded-xl',
      },
      
      state: {
        default: '',
        error: `
          border-error-500 dark:border-error-400
          focus:border-error-500 dark:focus:border-error-400
          focus:ring-error-500 dark:focus:ring-error-400
        `,
        success: `
          border-success-500 dark:border-success-400
          focus:border-success-500 dark:focus:border-success-400
          focus:ring-success-500 dark:focus:ring-success-400
        `,
        warning: `
          border-warning-500 dark:border-warning-400
          focus:border-warning-500 dark:focus:border-warning-400
          focus:ring-warning-500 dark:focus:ring-warning-400
        `,
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      state: 'default',
    },
  }
);

/**
 * Label variants
 */
const labelVariants = cva(
  'block font-medium text-neutral-700 dark:text-neutral-300',
  {
    variants: {
      size: {
        sm: 'text-sm mb-1',
        md: 'text-sm mb-2',
        lg: 'text-base mb-2',
        xl: 'text-lg mb-3',
      },
      required: {
        true: "after:content-['*'] after:ml-0.5 after:text-error-500",
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      required: false,
    },
  }
);

/**
 * Helper text variants
 */
const helperTextVariants = cva(
  'text-sm mt-1.5',
  {
    variants: {
      state: {
        default: 'text-neutral-500 dark:text-neutral-400',
        error: 'text-error-600 dark:text-error-400',
        success: 'text-success-600 dark:text-success-400',
        warning: 'text-warning-600 dark:text-warning-400',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

/**
 * Input component props
 */
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Success message */
  success?: string;
  /** Warning message */
  warning?: string;
  /** Icon to show before the input */
  leftIcon?: ReactNode;
  /** Icon to show after the input */
  rightIcon?: ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Container CSS classes */
  containerClassName?: string;
  /** Whether the field is required */
  isRequired?: boolean;
}

/**
 * Loading spinner for input
 */
const InputSpinner: React.FC = () => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
  />
);

/**
 * Input Component
 * A comprehensive input component with multiple variants, states, and smooth animations
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      warning,
      leftIcon,
      rightIcon,
      loading = false,
      variant = 'default',
      size = 'md',
      state = 'default',
      className,
      containerClassName,
      isRequired = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    
    // Determine the current state based on props
    const currentState = error ? 'error' : success ? 'success' : warning ? 'warning' : state;
    
    // Get the message to display
    const message = error || success || warning || helperText;
    
    // Determine if the input is interactive
    const isInteractive = !disabled && !loading;

    return (
      <motion.div
        className={cn('relative', containerClassName)}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Label */}
        {label && (
          <motion.label
            className={cn(labelVariants({ size, required: isRequired }))}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {label}
          </motion.label>
        )}
        
        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 pointer-events-none">
              <div className="w-4 h-4 flex items-center justify-center">
                {leftIcon}
              </div>
            </div>
          )}
          
          {/* Input */}
          <div className="relative">
            <input
              ref={ref}
              className={cn(
                inputVariants({ variant, size, state: currentState }),
                leftIcon && 'pl-10',
                (rightIcon || loading) && 'pr-10',
                className
              )}
              disabled={disabled || loading}
              onFocus={(e) => {
                setIsFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                props.onBlur?.(e);
              }}
              {...props}
            />
            
            {/* Motion wrapper for hover effect */}
            {isInteractive && (
              <motion.div
                className="absolute inset-0 pointer-events-none"
                whileFocus={{ scale: 1.01 }}
                transition={{ duration: 0.1 }}
              />
            )}
          </div>
          
          {/* Right Icon / Loading */}
          {(rightIcon || loading) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500">
              <div className="w-4 h-4 flex items-center justify-center">
                {loading ? <InputSpinner /> : rightIcon}
              </div>
            </div>
          )}
          
          {/* Focus Ring Animation */}
          <AnimatePresence>
            {isFocused && isInteractive && (
              <motion.div
                className="absolute inset-0 rounded-[inherit] border-2 border-primary-500 dark:border-primary-400 pointer-events-none"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 0.5, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              />
            )}
          </AnimatePresence>
        </div>
        
        {/* Helper Text / Error / Success / Warning */}
        <AnimatePresence mode="wait">
          {message && (
            <motion.p
              className={cn(helperTextVariants({ state: currentState }))}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              key={message}
            >
              {message}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

Input.displayName = 'Input';

/**
 * Textarea Component
 * Extended textarea with the same design system
 */
interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Success message */
  success?: string;
  /** Warning message */
  warning?: string;
  /** Loading state */
  loading?: boolean;
  /** Input variant */
  variant?: 'default' | 'filled' | 'outlined' | 'ghost';
  /** Input size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Input state */
  state?: 'default' | 'error' | 'success' | 'warning';
  /** Additional CSS classes */
  className?: string;
  /** Container CSS classes */
  containerClassName?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Number of visible text lines */
  rows?: number;
  /** Auto-resize functionality */
  autoResize?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      warning,
      loading = false,
      variant = 'default',
      size = 'md',
      state = 'default',
      className,
      containerClassName,
      isRequired = false,
      disabled,
      rows = 3,
      autoResize = false,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    
    // Determine the current state based on props
    const currentState = error ? 'error' : success ? 'success' : warning ? 'warning' : state;
    
    // Get the message to display
    const message = error || success || warning || helperText;
    
    // Determine if the input is interactive
    const isInteractive = !disabled && !loading;

    return (
      <motion.div
        className={cn('relative', containerClassName)}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Label */}
        {label && (
          <motion.label
            className={cn(labelVariants({ size, required: isRequired }))}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {label}
          </motion.label>
        )}
        
        {/* Textarea Container */}
        <div className="relative">
          <div className="relative">
            <textarea
              ref={ref}
              rows={rows}
              className={cn(
                inputVariants({ variant, size, state: currentState }),
                'resize-y min-h-[80px]',
                autoResize && 'resize-none',
                loading && 'pr-10',
                className
              )}
              disabled={disabled || loading}
              onFocus={(e) => {
                setIsFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                props.onBlur?.(e);
              }}
              {...props}
            />
            
            {/* Motion wrapper for hover effect */}
            {isInteractive && (
              <motion.div
                className="absolute inset-0 pointer-events-none"
                whileFocus={{ scale: 1.01 }}
                transition={{ duration: 0.1 }}
              />
            )}
          </div>
          
          {/* Loading Indicator */}
          {loading && (
            <div className="absolute right-3 top-3 text-neutral-400 dark:text-neutral-500">
              <InputSpinner />
            </div>
          )}
          
          {/* Focus Ring Animation */}
          <AnimatePresence>
            {isFocused && isInteractive && (
              <motion.div
                className="absolute inset-0 rounded-[inherit] border-2 border-primary-500 dark:border-primary-400 pointer-events-none"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 0.5, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              />
            )}
          </AnimatePresence>
        </div>
        
        {/* Helper Text / Error / Success / Warning */}
        <AnimatePresence mode="wait">
          {message && (
            <motion.p
              className={cn(helperTextVariants({ state: currentState }))}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
              key={message}
            >
              {message}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

Textarea.displayName = 'Textarea';

/**
 * Input Group Component
 * Groups multiple inputs together
 */
interface InputGroupProps {
  children: ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  spacing?: 'sm' | 'md' | 'lg';
}

export const InputGroup: React.FC<InputGroupProps> = ({
  children,
  className,
  orientation = 'vertical',
  spacing = 'md',
}) => {
  const spacingMap = {
    sm: orientation === 'horizontal' ? 'gap-2' : 'space-y-2',
    md: orientation === 'horizontal' ? 'gap-4' : 'space-y-4',
    lg: orientation === 'horizontal' ? 'gap-6' : 'space-y-6',
  };

  return (
    <div
      className={cn(
        orientation === 'horizontal' ? 'flex' : 'flex flex-col',
        spacingMap[spacing],
        className
      )}
    >
      {children}
    </div>
  );
};

export default Input; 